/**
 * EmojiPicker - wlasny, lekki wybor emoji dla komunikatora.
 *
 * BEZ zadnej zaleznosci z npm (emoji-mart itp.) - emoji sa w kodzie jako zwykle
 * znaki UTF-8, dokladnie tak jak robil to nieaktywny juz stary komunikator
 * (components/Messenger.jsx, skasowany przy wdrozeniu tego modulu).
 * Cala "biblioteka" to jedna tablica.
 *
 * Uwaga o stylach: motyw V3SYS wymusza `background: var(--card-bg) !important`
 * na kazdym <button>, ktory NIE ma tla podanego INLINE (v3sys-theme.css:962).
 * Dlatego kazdy przycisk tutaj ma `style={{ background: ... }}` - inaczej
 * zaznaczenie kategorii byloby niewidoczne.
 */

import React, { useEffect, useRef, useState } from 'react';

const KATEGORIE = [
  {
    id: 'buzki',
    etykieta: 'Buźki',
    ikona: '😀',
    emoji: [
      '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
      '🙂', '😉', '😊', '😇', '🥰', '😍', '😘', '😋',
      '😎', '🤓', '🧐', '🤔', '🤨', '😐', '😴', '🥱',
      '😮', '😯', '😲', '🙄', '😬', '😳', '🥵', '🥶',
      '😢', '😭', '😤', '😠', '😡', '🤯', '😱', '🤗',
      '🤝', '🙃', '😶', '🤐', '🤒', '🤕', '🥳', '😷',
    ],
  },
  {
    id: 'gesty',
    etykieta: 'Gesty',
    ikona: '👍',
    emoji: [
      '👍', '👎', '👌', '✌️', '🤞', '🤙', '👋', '🤚',
      '✋', '🖐️', '🙏', '👏', '🙌', '💪', '🤛', '🤜',
      '☝️', '👆', '👇', '👉', '👈', '✍️', '🤲', '🫡',
      '🧑', '👤', '👥', '🚶', '🏃', '💃', '🕺', '🤦',
    ],
  },
  {
    id: 'praca',
    etykieta: 'Praca',
    ikona: '🛒',
    emoji: [
      '🛒', '🏪', '🏬', '📦', '📥', '📤', '🚚', '🚛',
      '💰', '💵', '💳', '🧾', '🏷️', '📊', '📈', '📉',
      '📋', '📝', '🗒️', '📅', '📆', '⏰', '⏳', '☎️',
      '📞', '📱', '💻', '🖨️', '🖥️', '🔑', '🔒', '🔓',
      '🧹', '🧽', '🧰', '🔧', '⚙️', '🪜', '📐', '✂️',
      '☕', '🍕', '🥪', '🍽️', '🚻', '🚨', '⚡', '🔥',
    ],
  },
  {
    id: 'serca',
    etykieta: 'Serca',
    ikona: '❤️',
    emoji: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘',
      '🌹', '🌷', '🌸', '🌻', '🎉', '🎊', '🎁', '🥂',
    ],
  },
  {
    id: 'symbole',
    etykieta: 'Symbole',
    ikona: '✅',
    emoji: [
      '✅', '☑️', '✔️', '❌', '⛔', '🚫', '❗', '❓',
      '⚠️', '💯', '🔴', '🟠', '🟡', '🟢', '🔵', '⚫',
      '⭐', '🌟', '💡', '🔔', '🔕', '📌', '📍', '🔗',
      '➕', '➖', '✳️', '🆗', '🆕', '🔝', '🔄', '♻️',
      '🥇', '🥈', '🥉', '🏆', '🎯', '🧠', '👀', '💬',
    ],
  },
];

const EmojiPicker = ({ onWybierz, onZamknij }) => {
  const [kategoria, setKategoria] = useState(KATEGORIE[0].id);
  const kontenerRef = useRef(null);

  // Zamkniecie klikiem obok i Escape. Cleanup zdejmuje OBA nasluchy.
  useEffect(() => {
    const pozaPanelem = (e) => {
      if (kontenerRef.current && !kontenerRef.current.contains(e.target)) {
        if (onZamknij) onZamknij();
      }
    };
    const naKlawisz = (e) => {
      if (e.key === 'Escape' && onZamknij) onZamknij();
    };
    // setTimeout: bez tego to samo klikniecie, ktore otworzylo picker,
    // natychmiast by go zamknelo
    const t = setTimeout(() => document.addEventListener('mousedown', pozaPanelem), 0);
    document.addEventListener('keydown', naKlawisz);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', pozaPanelem);
      document.removeEventListener('keydown', naKlawisz);
    };
  }, [onZamknij]);

  const aktywna = KATEGORIE.find((k) => k.id === kategoria) || KATEGORIE[0];

  return (
    <div className="kom-emoji" ref={kontenerRef} role="dialog" aria-label="Wybór emoji">
      <div className="kom-emoji-kategorie">
        {KATEGORIE.map((k) => {
          const wybrana = k.id === kategoria;
          return (
            <button
              key={k.id}
              type="button"
              title={k.etykieta}
              aria-label={k.etykieta}
              aria-pressed={wybrana}
              onClick={() => setKategoria(k.id)}
              className="kom-emoji-kategoria"
              style={{
                background: wybrana ? 'var(--primary)' : 'var(--card-bg)',
                border: `1px solid ${wybrana ? 'var(--primary)' : 'var(--border)'}`,
                color: wybrana ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {k.ikona}
            </button>
          );
        })}
      </div>

      <div className="kom-emoji-siatka">
        {aktywna.emoji.map((e, i) => (
          <button
            key={`${aktywna.id}-${i}`}
            type="button"
            className="kom-emoji-znak"
            style={{ background: 'transparent', border: '1px solid transparent' }}
            onClick={() => onWybierz && onWybierz(e)}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
};

export default EmojiPicker;
